import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';

/**
 * Card Component
 * Solid, bordered surface container. Legacy glass variant names are kept as
 * aliases (glassmorphism retired 2026-07) so existing call sites still work.
 */

const cardVariants = cva(
  'rounded-xl transition-colors duration-150 overflow-hidden',
  {
    variants: {
      variant: {
        // Legacy aliases — now solid surfaces
        glass: 'bg-card border border-border',
        'glass-medium': 'bg-popover border border-border',
        'glass-strong': 'bg-muted border border-border',

        // Solid variants
        solid: 'bg-card border border-border',
        elevated: 'bg-card border border-border shadow-elevation-2',

        // Legacy gradient aliases — solid
        'gradient-glass': 'bg-card border border-border',
        'gradient-primary': 'bg-primary/[0.06] border border-primary/[0.15]',

        // Outline
        outline: 'border border-border bg-transparent hover:bg-card',
      },
      hover: {
        none: '',
        lift: 'hover:-translate-y-0.5 hover:shadow-elevation-2',
        scale: 'hover:shadow-elevation-2',
      },
      padding: {
        none: 'p-0',
        sm: 'p-4',
        md: 'p-6',
        lg: 'p-8',
        xl: 'p-10',
      },
      animated: {
        true: 'animate-scale-in',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'solid',
      hover: 'none',
      padding: 'md',
      animated: false,
    },
  }
);

const Card = React.forwardRef(({
  className,
  variant,
  hover,
  padding,
  animated,
  children,
  onClick,
  ...props
}, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        cardVariants({ variant, hover, padding, animated }),
        onClick && 'cursor-pointer',
        className
      )}
      onClick={onClick}
      {...props}
    >
      {children}
    </div>
  );
});

Card.displayName = 'Card';

/**
 * Card Header Component
 */
export const CardHeader = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col space-y-1.5', className)}
    {...props}
  >
    {children}
  </div>
));

CardHeader.displayName = 'CardHeader';

/**
 * Card Title Component
 */
export const CardTitle = React.forwardRef(({ className, children, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-tight tracking-tight text-foreground',
      className
    )}
    {...props}
  >
    {children}
  </h3>
));

CardTitle.displayName = 'CardTitle';

/**
 * Card Description Component
 */
export const CardDescription = React.forwardRef(({ className, children, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  >
    {children}
  </p>
));

CardDescription.displayName = 'CardDescription';

/**
 * Card Content Component
 */
export const CardContent = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('pt-0', className)}
    {...props}
  >
    {children}
  </div>
));

CardContent.displayName = 'CardContent';

/**
 * Card Footer Component
 */
export const CardFooter = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center pt-0', className)}
    {...props}
  >
    {children}
  </div>
));

CardFooter.displayName = 'CardFooter';

export default Card;
